"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { ScrollRail } from "@/components/home/ScrollRail";

export type ExploreCard = {
  name: string;
  tag: string;
  blurb: string;
  image: string;
  href: string;
};

type Tab = "destination" | "traveller" | "season";

// `short` is what phones show: the three full labels need ~330px in Poppins
// bold and a 320px screen has 288px, so the repeated "By" is dropped there —
// the tablist itself is labelled "Explore by" for screen readers.
const TABS: { key: Tab; label: string; short: string }[] = [
  { key: "destination", label: "By destination", short: "Destination" },
  { key: "traveller", label: "By traveller", short: "Traveller" },
  { key: "season", label: "By season", short: "Season" },
];

/** Gives each region its own identity color on the "By destination" cards,
 *  Ramble-style (they give their four holiday collections distinct colors
 *  rather than repeating one brand color) — light theme only. The var(--x,
 *  var(--accent)) fallback below means dark theme, which never defines
 *  these --region-* variables, silently keeps its existing plain gold. */
const REGION_ACCENT_VAR: Record<string, string> = {
  "Gilgit-Baltistan": "--region-gilgit-baltistan",
  "Azad Jammu and Kashmir": "--region-azad-kashmir",
  "Khyber Pakhtunkhwa": "--region-khyber-pakhtunkhwa",
  Punjab: "--region-punjab",
  Sindh: "--region-sindh",
  Balochistan: "--region-balochistan",
  Islamabad: "--region-islamabad",
};

/** "By destination / By traveller / By season" tabbed carousel for the
 *  homepage regions section - same card row, three different lenses on it.
 *  Folds in what used to be the standalone "When to Go" section (now the
 *  "season" tab) so seasonal browsing isn't duplicated elsewhere on the page.
 *  Cards show their title below the image (not overlaid) so the switcher
 *  itself, not a scrim, carries the visual weight. */
export function RegionExploreTabs({
  regions,
  travellers,
  seasons,
  regionCounts,
}: {
  regions: ExploreCard[];
  travellers: ExploreCard[];
  seasons: ExploreCard[];
  regionCounts: Record<string, string>;
}) {
  const [tab, setTab] = useState<Tab>("destination");
  const active = tab === "destination" ? regions : tab === "traveller" ? travellers : seasons;

  return (
    <div>
      <div
        role="tablist"
        aria-label="Explore by"
        // max-w-full + the tighter phone padding below keep the three tabs
        // inside a 288px column (320px screen); at desktop padding the row was
        // 334px and pushed the whole page sideways.
        className="mb-7 inline-flex max-w-full gap-1 rounded-full border border-[var(--border-subtle)] bg-[color-mix(in_srgb,var(--surface)_55%,transparent)] p-1"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className="cursor-pointer whitespace-nowrap rounded-full px-3 py-2.5 text-[13px] font-bold transition-colors duration-150 sm:px-5 sm:text-sm"
            style={{
              background: tab === t.key ? "var(--cta)" : "transparent",
              color: tab === t.key ? "#2b2b2b" : "var(--muted)",
            }}
          >
            <span className="sm:hidden">{t.short}</span>
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      <ScrollRail>
        {active.map((item) => {
          const regionVar = tab === "destination" ? REGION_ACCENT_VAR[item.name] : undefined;
          const regionColor = regionVar ? `var(${regionVar}, var(--accent))` : "var(--accent)";
          return (
          <Link
            key={item.name}
            href={item.href}
            className="group block w-[72vw] shrink-0 snap-start no-underline sm:w-[270px]"
          >
            <div
              className={regionVar ? "relative aspect-[4/5] overflow-hidden rounded-lg border-2" : "relative aspect-[4/5] overflow-hidden rounded-lg border border-[var(--border-subtle)]"}
              style={regionVar ? { borderColor: regionColor } : undefined}
            >
              <Image
                src={item.image}
                alt={item.name}
                fill
                quality={70}
                className="object-cover transition-transform duration-700 group-hover:scale-105"
                sizes="(max-width:640px) 72vw, 270px"
              />
            </div>
            <div className="pt-3.5">
              <p className="m-0 text-[10px] font-bold tracking-[0.18em] uppercase" style={{ color: regionColor }}>{item.tag}</p>
              <h3 className="mt-1.5 mb-0 font-[family-name:var(--font-display)] text-[1.2rem] font-medium text-[var(--foreground)] group-hover:text-[var(--accent)]">
                {item.name}
              </h3>
              <p className="mt-1.5 mb-0 text-[13px] leading-snug text-[var(--muted)]">{item.blurb}</p>
              {tab === "destination" && regionCounts[item.name] ? (
                <p className="mt-2 mb-0 text-[11px] font-semibold tracking-wide text-[var(--muted)]">{regionCounts[item.name]}</p>
              ) : null}
            </div>
          </Link>
          );
        })}
      </ScrollRail>
    </div>
  );
}
